import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { Send, Package, Calendar, CheckSquare } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ASCDispatch = () => {
  const { asc_id } = useParams();
  const navigate = useNavigate();
  
  const [ascInfo, setAscInfo] = useState(null);
  const [availableDevices, setAvailableDevices] = useState([]);
  const [selectedDevices, setSelectedDevices] = useState([]);
  const [expectedReturnDate, setExpectedReturnDate] = useState('');
  const [remarks, setRemarks] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (asc_id) {
      loadASCInfo();
    }
    loadAvailableDevices();
  }, [asc_id]);

  const loadASCInfo = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/asc/${asc_id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setAscInfo(response.data.asc);
      }
    } catch (error) {
      console.error('Error loading ASC info:', error);
    }
  };

  const loadAvailableDevices = async () => {
    try {
      const token = localStorage.getItem('token');
      // Get devices that need external repair (could be re_pna, ber, nr status)
      const response = await axios.get(
        `${API_URL}/api/trc/devices?status=re_pna`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setAvailableDevices(response.data.devices);
      }
    } catch (error) {
      console.error('Error loading devices:', error);
    }
  };

  const toggleDevice = (deviceId) => {
    if (selectedDevices.includes(deviceId)) {
      setSelectedDevices(selectedDevices.filter(id => id !== deviceId));
    } else {
      setSelectedDevices([...selectedDevices, deviceId]);
    }
  };

  const handleDispatch = async () => {
    if (selectedDevices.length === 0) {
      alert('Please select at least one device');
      return;
    }
    if (!expectedReturnDate) {
      alert('Please set expected return date');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/asc/dispatch`,
        {
          device_ids: selectedDevices,
          asc_id: asc_id,
          parts_sent: [],
          expected_return_date: expectedReturnDate,
          remarks: remarks
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        alert(`Dispatched ${selectedDevices.length} devices to ASC!`);
        navigate('/asc/dispatches');
      }
    } catch (error) {
      console.error('Error dispatching:', error);
      alert(error.response?.data?.detail || 'Error dispatching devices');
    } finally {
      setLoading(false);
    }
  };

  // Set default date (7 days from now)
  useEffect(() => {
    const defaultDate = new Date();
    defaultDate.setDate(defaultDate.getDate() + 7);
    setExpectedReturnDate(defaultDate.toISOString().split('T')[0]);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Send className="w-8 h-8 text-blue-600" />
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Dispatch to ASC</h1>
              {ascInfo && <p className="text-gray-600">{ascInfo.name}</p>}
            </div>
          </div>
          <button
            onClick={() => navigate('/asc/manage')}
            className="px-4 py-2 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
          >
            Back
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Device Selection */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-900">
                  Select Devices ({selectedDevices.length} selected)
                </h2>
                <button
                  onClick={() => {
                    if (selectedDevices.length === availableDevices.length) {
                      setSelectedDevices([]);
                    } else {
                      setSelectedDevices(availableDevices.map(d => d.trc_device_id));
                    }
                  }}
                  className="px-4 py-2 border-2 border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2"
                >
                  <CheckSquare className="w-4 h-4" />
                  {selectedDevices.length === availableDevices.length ? 'Deselect All' : 'Select All'}
                </button>
              </div>

              {availableDevices.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Package className="w-16 h-16 mx-auto mb-3 text-gray-300" />
                  <p>No devices available for ASC dispatch</p>
                </div>
              ) : (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {availableDevices.map((device, index) => {
                    const isSelected = selectedDevices.includes(device.trc_device_id);

                    return (
                      <div
                        key={index}
                        onClick={() => toggleDevice(device.trc_device_id)}
                        className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                          isSelected
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => {}}
                            className="w-5 h-5"
                          />
                          <div className="flex-1">
                            <div className="font-bold text-gray-900">{device.did}</div>
                            <div className="text-sm text-gray-600">
                              {device.brand} {device.model}
                            </div>
                            <div className="text-xs text-gray-500">
                              TRC ID: {device.trc_device_id}
                            </div>
                          </div>
                          <div>
                            <span className="px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded">
                              {device.status}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Dispatch Details */}
          <div className="space-y-6">
            {/* ASC Info */}
            {ascInfo && (
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">ASC Details</h3>
                <div className="space-y-2 text-sm">
                  <div>
                    <span className="text-gray-600">Name:</span>
                    <div className="font-semibold">{ascInfo.name}</div>
                  </div>
                  <div>
                    <span className="text-gray-600">Code:</span>
                    <div className="font-semibold">{ascInfo.code}</div>
                  </div>
                  <div>
                    <span className="text-gray-600">Location:</span>
                    <div className="font-semibold">{ascInfo.city}, {ascInfo.state}</div>
                  </div>
                  <div>
                    <span className="text-gray-600">Contact:</span>
                    <div className="font-semibold">{ascInfo.mobile}</div>
                  </div>
                </div>
              </div>
            )}

            {/* Dispatch Form */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Dispatch Details</h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Expected Return Date *
                  </label>
                  <input
                    type="date"
                    value={expectedReturnDate}
                    onChange={(e) => setExpectedReturnDate(e.target.value)}
                    className="w-full px-4 py-2 border-2 rounded-lg"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Remarks
                  </label>
                  <textarea
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                    rows="3"
                    placeholder="Any special instructions..."
                    className="w-full px-4 py-2 border-2 rounded-lg"
                  />
                </div>

                <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-sm text-blue-900">
                    <strong>{selectedDevices.length} device(s)</strong> will be dispatched to this ASC
                  </p>
                </div>

                <button
                  onClick={handleDispatch}
                  disabled={loading || selectedDevices.length === 0}
                  className="w-full py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 disabled:bg-gray-300 flex items-center justify-center gap-2"
                >
                  <Send className="w-5 h-5" />
                  {loading ? 'Dispatching...' : 'Dispatch to ASC'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ASCDispatch;
